import { useCallback, useRef, useEffect } from "react";
import { getAuthorizationMessages } from "../api";
import { AuthorizationStatus } from "../types/enums";

interface UseAuthorizationPollingOptions {
  config: IWidgetConfig;
  currentRequestRef: React.MutableRefObject<PlaceBetSportPayload | null>;
  onAutoResubmit: (request: PlaceBetSportPayload, authKey: string) => void;
  onConfirmChanges: (message: AuthorizationMessage) => void;
  onDeclined: (message: AuthorizationMessage) => void;
  onExpired: () => void;
  onCountdownTick: (secondsLeft: number) => void;
  onWaiting: (newSecondsLeft: number, message: AuthorizationMessage) => void;
  onError: (msg: string) => void;
}

export const useAuthorizationPolling = (
  options: UseAuthorizationPollingOptions,
) => {
  const pollingIntervalRef = useRef<any>(null);
  const countdownIntervalRef = useRef<any>(null);
  const secondsLeftRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleConfirmedMessage = useCallback(
    (message: AuthorizationMessage) => {
      if (!message.isChanged && options.currentRequestRef.current) {
        options.onAutoResubmit(
          options.currentRequestRef.current,
          message.authorizationKey,
        );
      } else if (message.isChanged) {
        options.onConfirmChanges(message);
      }
    },
    [options],
  );

  const handleMessageStatus = useCallback(
    (message: AuthorizationMessage) => {
      const isWaiting =
        message.status === AuthorizationStatus.Waiting ||
        message.status === AuthorizationStatus.Canceled;

      if (isWaiting) {
        const newSecondsLeft = message.validUntil
          ? Math.max(0, Math.floor(message.validUntil / 1000))
          : secondsLeftRef.current;
        secondsLeftRef.current = newSecondsLeft;
        options.onWaiting(newSecondsLeft, message);
        return;
      }

      stopPolling();
      switch (message.status) {
        case AuthorizationStatus.Expired:
          options.onExpired();
          break;
        case AuthorizationStatus.Declined:
          options.onDeclined(message);
          break;
        case AuthorizationStatus.Confirmed:
          handleConfirmedMessage(message);
          break;
      }
    },
    [options, stopPolling, handleConfirmedMessage],
  );

  const pollOnce = useCallback(
    async (authKey: string) => {
      try {
        const response = await getAuthorizationMessages({
          authorizationKeys: [authKey],
          config: {
            brandId: options.config.brand.id,
            channelId: options.config.channelId,
            terminal: options.config.brand.terminal,
            language: options.config.culture,
            userToken: options.config.user.token,
          },
        });
        if (response.data?.length) {
          handleMessageStatus(response.data[0]);
        }
      } catch {
        options.onError("Failed to check authorization status");
      }
    },
    [options, handleMessageStatus],
  );

  const startPolling = useCallback(
    (authKey: string) => {
      pollingIntervalRef.current = setInterval(() => pollOnce(authKey), 5000);
      pollOnce(authKey);
    },
    [pollOnce],
  );

  const startCountdown = useCallback(
    (timeoutMs: number) => {
      const secs = Math.floor(timeoutMs / 1000);
      secondsLeftRef.current = secs;
      countdownIntervalRef.current = setInterval(() => {
        const next = secondsLeftRef.current - 1;
        secondsLeftRef.current = next;
        options.onCountdownTick(Math.max(0, next));
        if (next <= 0) stopPolling();
      }, 1000);
    },
    [options, stopPolling],
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  return { startPolling, stopPolling, startCountdown };
};
